import axios from '@/libs/axios'

const ACTIVITY_TYPE_API_TO_SLUG = {
  'planning.simulacro': 'simulacrum',
  'Planning.Simulacro': 'simulacrum',
  'planning.evaluacion_riesgo': 'risk',
  'planning.ejercicio_operativo': 'operational_procedure',
  'planning.auto_evaluacion_seguridad': 'security_self_audit',
}

const KNOWN_SLUGS = ['risk', 'simulacrum', 'operational_procedure', 'security_self_audit']

const ACTIVITY_LABEL_OR_ALIAS_TO_SLUG = {
  simulacro: 'simulacrum',
  'evaluación de riesgo': 'risk',
  'evaluacion de riesgo': 'risk',
  'ejercicio operativo': 'operational_procedure',
  'auto evaluación de seguridad': 'security_self_audit',
  'auto evaluacion de seguridad': 'security_self_audit',
}

function toNumberOrNull(value) {
  if (value == null || value === '') return null
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

export function normalizePlanningActivitySlugFromApi(val) {
  if (val == null) return null
  const stringVal = String(val).trim()
  if (KNOWN_SLUGS.includes(stringVal)) return stringVal
  const lower = stringVal.toLowerCase()
  if (KNOWN_SLUGS.includes(lower)) return lower
  const fromLabel = ACTIVITY_LABEL_OR_ALIAS_TO_SLUG[lower]
  if (fromLabel) return fromLabel
  return ACTIVITY_TYPE_API_TO_SLUG[stringVal] ?? ACTIVITY_TYPE_API_TO_SLUG[lower] ?? stringVal
}

export function resolvePlanningActivitySlugFromStoreItem(item) {
  if (!item || typeof item !== 'object') return null
  const raw =
    typeof item.activity_type === 'string'
      ? item.activity_type
      : (item.activity_type && item.activity_type.name)
      ?? item.activity_type_name
      ?? null
  return normalizePlanningActivitySlugFromApi(raw)
}

function extractUsersList(payload) {
  if (payload == null) return []
  if (Array.isArray(payload)) return payload
  if (typeof payload !== 'object') return []
  if (Array.isArray(payload.data)) return payload.data
  const inner = payload.data
  if (inner && typeof inner === 'object' && Array.isArray(inner.data)) return inner.data
  return []
}

async function getUserList(url) {
  const { data } = await axios.get(url)
  return extractUsersList(data)
}

async function getUserListOrEmpty(url) {
  try {
    return await getUserList(url)
  } catch (_) {
    return []
  }
}

function normalizeIdentityValue(value) {
  if (value == null) return ''
  return String(value).trim().toLowerCase()
}

function resolveUserIdentityKey(user) {
  if (!user || typeof user !== 'object') return null
  const numericId = toNumberOrNull(user.id != null ? user.id : user.user_id)
  if (numericId != null) return `id:${numericId}`
  const stringId = normalizeIdentityValue(user.id != null ? user.id : user.user_id)
  if (stringId) return `id:${stringId}`
  const email = normalizeIdentityValue(user.email)
  if (email) return `email:${email}`
  const username = normalizeIdentityValue(user.user != null ? user.user : user.username)
  if (username) return `username:${username}`
  return null
}

function mergeUniqueUsers(userLists) {
  const uniqueUsersByIdentity = new Map()
  const list = Array.isArray(userLists) ? userLists : []
  list.forEach((users) => {
    const usersList = Array.isArray(users) ? users : []
    usersList.forEach((userRow) => {
      const identityKey = resolveUserIdentityKey(userRow)
      if (!identityKey) return
      if (uniqueUsersByIdentity.has(identityKey)) return
      uniqueUsersByIdentity.set(identityKey, userRow)
    })
  })
  return [...uniqueUsersByIdentity.values()]
}

async function fetchSimulacrumReviewers() {
  const primary = await getUserListOrEmpty('/v1/simulacrums/getReviewers')
  if (primary.length > 0) return primary
  return getUserListOrEmpty('/v1/risk_evaluations/getReviewers')
}

async function fetchSecuritySelfAuditReviewers() {
  const primary = await getUserListOrEmpty('/v1/security_self_audits/reviewers')
  if (primary.length > 0) return primary
  return getUserListOrEmpty('/v1/security_self_audits_reviewers')
}

export async function fetchPlanningFirstReviewersByActivityType(activitySlug) {
  const resolved = normalizePlanningActivitySlugFromApi(activitySlug)
  const slug = resolved != null ? String(resolved).trim() : ''

  if (slug === 'simulacrum') {
    return fetchSimulacrumReviewers()
  }

  if (slug === 'security_self_audit') {
    const securitySelfAuditReviewers = await fetchSecuritySelfAuditReviewers()
    const simulacrumReviewers = await fetchSimulacrumReviewers()
    return mergeUniqueUsers([securitySelfAuditReviewers, simulacrumReviewers])
  }

  if (slug === 'operational_procedure') {
    return getUserListOrEmpty('/v1/operational_procedure-get-reviewers')
  }

  if (slug === 'risk') {
    return getUserListOrEmpty('/v1/risk_evaluations/getReviewers')
  }

  try {
    const { data } = await axios.get('v1/planning-getUserReview')
    return extractUsersList(data)
  } catch (_) {
    return []
  }
}

export async function fetchPlanningFirstReviewersForStoreCurrentItem(item) {
  return fetchPlanningFirstReviewersByActivityType(resolvePlanningActivitySlugFromStoreItem(item))
}
